import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import { RootState } from "src/store";
import { makeStyles } from "tss-react/mui";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { useCurrentSubscription } from "@dashboard/workspaces/api/helpers";
import { Box, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import ApmLink from "@shared/ApmLink";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { LoadingBox } from "@shared/Loading";
import { TextLink } from "@shared/TextLink";

import { selectDevicesByGroupIds } from "../devices/store/selectors/devices";

import DownloadsTable from "./components/DownloadsTable";
import { LegacyInstallersDialog } from "./components/LegacyInstallersDialog";
import { useReleaseFiles } from "./hooks/useReleaseFiles";

const useStyles = makeStyles()((theme) => ({
    contentLayout: {
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
    },
    toolbar: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        flexShrink: 0,
    },
    tutorialAlert: { marginBottom: theme.spacing(4), flexShrink: 0 },
    tutorialList: { listStyleType: "disc", marginLeft: theme.spacing(6), marginTop: theme.spacing(2) },
}));

const DownloadsPage = () => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages"]);
    const { data: currentSubscription } = useCurrentSubscription({});
    const groupIds = currentSubscription?.groupIds || [];
    const devices = useSelector((state: RootState) => selectDevicesByGroupIds(state, groupIds));
    const releaseFiles = useReleaseFiles();

    const [legacyDialogOpen, setLegacyDialogOpen] = React.useState(false);

    return (
        <PageContainer fitViewport>
            <PageHeader title={t("pages:downloadsPage.title")} description={t("pages:downloadsPage.description")} />
            <ExpiredWrapper>
                <Box className={classes.contentLayout}>
                    <Box className={classes.toolbar}>
                        <TextLink
                            component="button"
                            type="button"
                            onClick={() => setLegacyDialogOpen(true)}
                            tooltip={t("pages:downloadsPage.legacyInstallers.tooltip")}
                        >
                            {t("pages:downloadsPage.legacyInstallers.triggerLink")}
                        </TextLink>
                    </Box>

                    {!devices.length ? (
                        <CustomAlert severity="info" className={classes.tutorialAlert}>
                            <Typography>{t("pages:downloadsPage.tutorial.title")}</Typography>
                            <ul className={classes.tutorialList}>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title="How to finish device configuration"
                                            href="https://emteria.com/kb/initial-configuration-setup-wizard"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.configuration")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                                <li>
                                    <Typography>
                                        <ApmLink
                                            parent="Downloads page"
                                            title="How to enable remote device management"
                                            href="https://emteria.com/kb/enable-mdm"
                                        >
                                            {t("pages:downloadsPage.tutorial.items.management")}
                                        </ApmLink>
                                    </Typography>
                                </li>
                            </ul>
                        </CustomAlert>
                    ) : null}

                    {releaseFiles.status === "pending" ? (
                        <LoadingBox style={{ flex: 1, minHeight: 0 }} />
                    ) : (
                        <DownloadsTable data={releaseFiles.data} />
                    )}
                </Box>
            </ExpiredWrapper>
            <LegacyInstallersDialog open={legacyDialogOpen} onClose={() => setLegacyDialogOpen(false)} />
        </PageContainer>
    );
};
export default withMetaDescription(DownloadsPage, {
    title: i18next.t("pages:downloadsPage.metaTitle"),
    description: i18next.t("pages:downloadsPage.metaDescription"),
});
